'use client'

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react'

import {
  featureBadgeEventProps,
  pageFeatureBadge,
  type PageFeatureBadge,
} from './analytics'
import type { FeatureBadgeKey } from './constants'

// How many mounted surfaces show each badge, so one unmounting doesn't drop
// a badge another still shows
const PageFeatureBadgesContext = createContext<Map<
  FeatureBadgeKey,
  number
> | null>(null)

/** Collects the badges on screen so the page view can report them. */
export const PageFeatureBadgesProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [badges] = useState(() => new Map<FeatureBadgeKey, number>())
  return (
    <PageFeatureBadgesContext.Provider value={badges}>
      {children}
    </PageFeatureBadgesContext.Provider>
  )
}

/**
 * Registers a shown badge for the page view. Layout effects run before every
 * passive effect in the same commit, so the badge is in place before the page
 * view fires, wherever it sits in the tree.
 */
export const useReportPageFeatureBadge = (
  featureKey: FeatureBadgeKey,
  pillShown: boolean
) => {
  const badges = useContext(PageFeatureBadgesContext)
  useLayoutEffect(() => {
    if (!badges || !pillShown) return
    badges.set(featureKey, (badges.get(featureKey) ?? 0) + 1)
    return () => {
      const count = (badges.get(featureKey) ?? 1) - 1
      if (count > 0) badges.set(featureKey, count)
      else badges.delete(featureKey)
    }
  }, [badges, featureKey, pillShown])
}

/** Reads the badges on screen: null, not [], when there are none. */
export const usePageFeatureBadges = () => {
  const badges = useContext(PageFeatureBadgesContext)
  return useCallback(
    (): PageFeatureBadge[] | null =>
      badges?.size ? Array.from(badges.keys(), pageFeatureBadge) : null,
    [badges]
  )
}

/**
 * Badge fields for a surface's own events, sent in every arm. Call the
 * returned function when the event fires: it reads whether the pill is on
 * screen then, without reading the badge flag (a read is an LD exposure).
 */
export const useFeatureBadgeEventProps = (featureKey: FeatureBadgeKey) => {
  const badges = useContext(PageFeatureBadgesContext)
  return useCallback(
    () =>
      featureBadgeEventProps(featureKey, {
        pillShown: badges?.has(featureKey) ?? false,
      }),
    [badges, featureKey]
  )
}
